import type { SortDescriptor } from '@workramp/ui';
import type { CMSAssetItem, CMSAssetLocation, CMSDetailTableColumn } from '../../types';
import { Body, Button, Icon, Table } from '@workramp/ui';
import { useState } from 'react';
import { sortBy } from 'underscore';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';
import { MediaObject } from '~/features/common';
import { DateText } from '~/features/temporal';
import { CMSEmptyTable } from '../CMSEmptyTable';

const { t } = I18n;

export const columns: CMSDetailTableColumn[] = [
  { name: t('js.admin.cms.locations_table.columns.location'), key: 'location', allowSorting: true },
  { name: t('js.admin.cms.locations_table.columns.added'), key: 'added', allowSorting: true },
];

const sortLocations = (locations: CMSAssetLocation[], sortDescriptor: SortDescriptor) => {
  const sortColumn = sortDescriptor.column;

  const sortFns: { [col: string]: (locs: CMSAssetLocation[]) => CMSAssetLocation[] } = {
    location: (locs) => sortBy(locs, 'libraryName'),
    added: (locs) => sortBy(locs, 'createdAt'),
  };
  const sortFn = sortFns[sortColumn];

  const sortedResult = sortFn(locations);

  return sortDescriptor.direction === 'ascending' ? sortedResult : sortedResult.reverse();
};

export interface CMSDetailsBodyProps {
  asset: CMSAssetItem;
}

export const CMSAssetLocationsTable = ({ asset }: CMSDetailsBodyProps) => {
  const { locations: assetLocations } = asset;
  const theme = useTheme();

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'added',
    direction: 'descending',
  });
  const handleSortChange = (descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor);
  };

  const sortedLocations = sortLocations(assetLocations, sortDescriptor);
  return (
    <Table
      sortDescriptor={sortDescriptor}
      onSortChange={handleSortChange}
      emptyState={
        <CMSEmptyTable
          asset={asset}
          body={t('js.admin.cms.locations_table.empty_description')}
          subheading={t('js.admin.cms.locations_table.empty_title')}
        />
      }
      rowPadding={theme.spacing(8)}
      data-testid="cms-details-table"
      showLastRowDivider
    >
      <Table.Header>
        {columns.map(({ name, key, allowSorting }) => (
          <Table.Column key={key} allowsSorting={allowSorting}>
            {name}
          </Table.Column>
        ))}
      </Table.Header>
      <Table.Body>
        {sortedLocations.map((row: CMSAssetLocation) => {
          const { libraryId, libraryName, createdAt, userName, libraryFolderName, libraryFolderId } = row;

          return (
            <Table.Row key={`${libraryId}-${createdAt}-${libraryFolderId}`}>
              <Table.Cell>
                <MediaObject
                  title={
                    <Button
                      aria-label={libraryName}
                      variant="text-inline"
                      onPress={() => {
                        let libraryLink = `/admin/universities/${libraryId}`;
                        if (libraryFolderId && libraryFolderId.length > 0) {
                          libraryLink += `?university_folder_id=${libraryFolderId}`;
                        }
                        window.location.href = libraryLink;
                      }}
                    >
                      {libraryName}
                    </Button>
                  }
                  description={<Body level="sm">{libraryFolderName}</Body>}
                  media={<Icon name="library:regular" color={theme.colors.decorative.purple[500]} />}
                />
              </Table.Cell>
              <Table.Cell>
                <Body level="md">
                  <DateText isLocalTime showTime dateStyle="medium" date={new Date(createdAt).valueOf()} /> Added by{' '}
                  {userName}
                </Body>
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};
